import { ArrowsAltOutlined, CheckOutlined, CopyOutlined, DownOutlined, LoadingOutlined, SearchOutlined, ShrinkOutlined, UpOutlined } from "@ant-design/icons";
import { message } from "antd";
import type { ReactNode } from "react";
import { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { formatJson } from "./jsonUtils";
import type { JsonPrimitive, JsonValue } from "./jsonUtils";

const CHILDREN_BATCH_SIZE = 100;

const valueType = (value: JsonValue) => {
    if (value === null) return "null";
    if (Array.isArray(value)) return "array";
    return typeof value;
};

const primitiveColor = (value: JsonPrimitive) => {
    if (value === null) return "text-slate-500";

    switch (typeof value) {
        case "string":
            return "text-emerald-400";
        case "number":
            return "text-sky-400";
        case "boolean":
            return "text-violet-400";
        default:
            return "text-slate-300";
    }
};

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const COLOR_PATTERN = /^(?:#[\da-f]{3,4}|#[\da-f]{6}|#[\da-f]{8}|rgba?\(\s*[\d.%]+\s*,\s*[\d.%]+\s*,\s*[\d.%]+(?:\s*,\s*[\d.]+)?\s*\)|hsla?\([^)]*\))$/i;
const MIN_TIMESTAMP = Date.UTC(2000, 0, 1);
const MAX_TIMESTAMP = Date.UTC(2100, 0, 1);
const dateOnlyFormatter = new Intl.DateTimeFormat("vi-VN", {
    dateStyle: "medium",
});
const dateTimeFormatter = new Intl.DateTimeFormat("vi-VN", {
    dateStyle: "medium",
    timeStyle: "medium",
});

const getDatePreview = (value: JsonPrimitive) => {
    let dateValue: Date | null = null;

    if (typeof value === "string" && ISO_DATE_PATTERN.test(value)) {
        dateValue = new Date(value);
    } else if (typeof value === "number") {
        const milliseconds = value < 10_000_000_000 ? value * 1000 : value;

        if (milliseconds >= MIN_TIMESTAMP && milliseconds < MAX_TIMESTAMP) {
            dateValue = new Date(milliseconds);
        }
    }

    if (!dateValue || Number.isNaN(dateValue.getTime())) return null;

    const isDateOnly = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
    return (isDateOnly ? dateOnlyFormatter : dateTimeFormatter).format(dateValue);
};

const PrimitiveValue = ({
    value,
    searchQuery = "",
}: {
    value: JsonPrimitive;
    searchQuery?: string;
}) => {
    const displayValue = typeof value === "string" ? `"${value}"` : String(value);
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const highlightedValue: ReactNode[] = [];
    const datePreview = getDatePreview(value);
    const colorPreview = typeof value === "string" && COLOR_PATTERN.test(value.trim()) ? value.trim() : null;

    if (normalizedSearchQuery) {
        const normalizedDisplayValue = displayValue.toLowerCase();
        let cursor = 0;
        let matchIndex = normalizedDisplayValue.indexOf(normalizedSearchQuery);

        while (matchIndex !== -1) {
            if (matchIndex > cursor) highlightedValue.push(displayValue.slice(cursor, matchIndex));
            highlightedValue.push(
                <mark className="rounded-sm bg-amber-200 px-0.5 text-slate-900" key={matchIndex}>
                    {displayValue.slice(matchIndex, matchIndex + normalizedSearchQuery.length)}
                </mark>,
            );
            cursor = matchIndex + normalizedSearchQuery.length;
            matchIndex = normalizedDisplayValue.indexOf(normalizedSearchQuery, cursor);
        }

        if (cursor < displayValue.length) highlightedValue.push(displayValue.slice(cursor));
    }

    return (
        <>
            <span className={`${primitiveColor(value)} whitespace-nowrap font-medium`}>
                {highlightedValue.length ? highlightedValue : displayValue}
            </span>
            {datePreview && (
                <span className="ml-1 rounded bg-slate-700/70 px-1.5 font-sans text-[11px] font-normal text-slate-300">
                    {datePreview}
                </span>
            )}
            {colorPreview && (
                <span
                    aria-label={`Color preview ${colorPreview}`}
                    className="ml-1 inline-block h-3.5 w-3.5 shrink-0 rounded-sm border border-white/30 shadow-sm"
                    style={{ backgroundColor: colorPreview }}
                    title={colorPreview}
                />
            )}
        </>
    );
};

interface TreeNodeProps {
    name?: string;
    value: JsonValue;
    path?: Array<string | number>;
    onNavigateToValue?: (path: Array<string | number>) => void;
    depth?: number;
    defaultExpanded?: boolean;
    expandAllSignal?: number;
    closeAllSignal?: number;
    expandSelfSignal?: number;
    expandSelfValue?: boolean;
    searchQuery?: string;
    showAllSearchResults?: boolean;
    activeSearchPath?: Array<string | number>;
}

const matchesDirectSearch = (value: JsonValue, query: string): boolean => {
    return value === null || typeof value !== "object"
        ? String(value).toLowerCase().includes(query)
        : false;
};

const matchesSearch = (value: JsonValue, query: string, name?: string): boolean => {
    if (name?.toLowerCase().includes(query) || matchesDirectSearch(value, query)) return true;
    if (value === null || typeof value !== "object") return false;

    return Object.entries(value).some(([key, childValue]) => matchesSearch(childValue, query, key));
};

const collectSearchPaths = (value: JsonValue, query: string): Array<Array<string | number>> => {
    const results: Array<Array<string | number>> = [];
    if (!query) return results;

    const visit = (item: JsonValue, path: Array<string | number>, name?: string) => {
        if (name?.toLowerCase().includes(query) || matchesDirectSearch(item, query)) results.push(path);
        if (item !== null && typeof item === "object") {
            Object.entries(item).forEach(([key, child]) => visit(child, [...path, Array.isArray(item) ? Number(key) : key], key));
        }
    };
    visit(value, []);
    return results;
};

const SearchableKey = ({ name, query }: { name: string; query: string }) => {
    if (!query) return <>{name}</>;
    const parts: ReactNode[] = [];
    let cursor = 0;
    let index = name.toLowerCase().indexOf(query);
    while (index !== -1) {
        parts.push(name.slice(cursor, index));
        parts.push(<mark className="rounded-sm bg-amber-200 px-0.5 text-slate-900" key={index}>{name.slice(index, index + query.length)}</mark>);
        cursor = index + query.length;
        index = name.toLowerCase().indexOf(query, cursor);
    }
    parts.push(name.slice(cursor));
    return <>{parts}</>;
};

const TreeNodeComponent = ({
    name,
    value,
    path = [],
    onNavigateToValue,
    depth = 0,
    defaultExpanded = true,
    expandAllSignal = 0,
    closeAllSignal = 0,
    expandSelfSignal = 0,
    expandSelfValue = true,
    searchQuery = "",
    showAllSearchResults = false,
    activeSearchPath,
}: TreeNodeProps) => {
    const isCollection = value !== null && typeof value === "object";
    const entries = isCollection ? Object.entries(value) : [];
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const containsDirectMatch = Boolean(
        normalizedSearchQuery
        && entries.some(([key, childValue]) => key.toLowerCase().includes(normalizedSearchQuery) || matchesDirectSearch(childValue, normalizedSearchQuery)),
    );
    const showEntireObject = showAllSearchResults || containsDirectMatch || Boolean(normalizedSearchQuery && name?.toLowerCase().includes(normalizedSearchQuery));
    const visibleEntries = searchQuery
        ? showEntireObject
            ? entries
            : entries.filter(([key, childValue]) => matchesSearch(childValue, normalizedSearchQuery, key))
        : entries;
    const rowRef = useRef<HTMLDivElement>(null);
    const isActiveSearchMatch = Boolean(activeSearchPath && JSON.stringify(path) === JSON.stringify(activeSearchPath));
    const containsActiveSearchMatch = Boolean(activeSearchPath && activeSearchPath.length > path.length && path.every((part, index) => part === activeSearchPath[index]));
    const activeChildKey = containsActiveSearchMatch ? String(activeSearchPath?.[path.length]) : undefined;
    const activeChildIndex = visibleEntries.findIndex(([key]) => key === activeChildKey);
    const [expanded, setExpanded] = useState(defaultExpanded && entries.length <= CHILDREN_BATCH_SIZE);
    const [visibleCount, setVisibleCount] = useState(CHILDREN_BATCH_SIZE);
    const [expandChildrenSignal, setExpandChildrenSignal] = useState(0);
    const [itemsExpanded, setItemsExpanded] = useState(false);
    const isArray = Array.isArray(value);
    const hasCollectionChildren = isArray && value.some(item => item !== null && typeof item === "object");
    const collectionType = Array.isArray(value) ? "Array" : "Object";
    const openingBracket = Array.isArray(value) ? "[" : "{";
    const closingBracket = Array.isArray(value) ? "]" : "}";

    useEffect(() => {
        if (!expandAllSignal || !isCollection) return;

        setExpanded(true);
        setVisibleCount(entries.length);
        setItemsExpanded(true);
    }, [expandAllSignal, entries.length, isCollection]);

    useEffect(() => {
        if (expandSelfSignal && isCollection) setExpanded(expandSelfValue);
    }, [expandSelfSignal, expandSelfValue, isCollection]);

    useEffect(() => {
        if (!closeAllSignal || !isCollection) return;

        setExpanded(false);
        setItemsExpanded(false);
    }, [closeAllSignal, isCollection]);

    useEffect(() => {
        if (searchQuery && visibleEntries.length && !showAllSearchResults) setExpanded(true);
    }, [searchQuery, showAllSearchResults, visibleEntries.length]);

    useEffect(() => {
        if (!showAllSearchResults || !isCollection) return;

        setExpanded(false);
        setItemsExpanded(false);
    }, [isCollection, showAllSearchResults]);

    useEffect(() => {
        if (containsActiveSearchMatch) {
            setExpanded(true);
            setVisibleCount(count => Math.max(count, activeChildIndex + 1));
        }
        if (isActiveSearchMatch) rowRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }, [activeSearchPath, containsActiveSearchMatch, activeChildIndex, isActiveSearchMatch]);

    if (!isCollection) {
        return (
            <div ref={rowRef} className={`flex h-6 items-center gap-2 rounded font-mono text-[15px] leading-6 ${isActiveSearchMatch ? "-ml-1.5 pl-1.5 bg-orange-500/20 ring-1 ring-orange-400/60" : ""}`}>
                {name !== undefined && <span className="text-slate-300"><SearchableKey name={name} query={normalizedSearchQuery} />:</span>}
                <span
                    className="cursor-pointer select-none rounded-sm hover:bg-white/10"
                    onDoubleClick={async () => {
                        try {
                            await navigator.clipboard.writeText(String(value));
                            message.success({ content: "Copied successfully", key: "object-tree-copy" });
                        } catch {
                            message.error({ content: "Không thể copy. Vui lòng kiểm tra quyền truy cập clipboard.", key: "object-tree-copy" });
                        }
                    }}
                    title="Double-click to copy this value"
                >
                    <PrimitiveValue searchQuery={searchQuery} value={value as JsonPrimitive} />
                </span>
                <span className="ml-1 text-[11px] font-normal text-slate-500">{valueType(value)}</span>
            </div>
        );
    }

    return (
        <div className="font-mono text-[15px] leading-6">
            <div ref={rowRef} className={`group -ml-6 flex h-6 w-[calc(100%+1.5rem)] items-center rounded-md pl-6 hover:bg-white/5 ${isActiveSearchMatch ? "bg-orange-500/20 ring-1 ring-orange-400/60" : ""}`}>
                <button
                    className="relative flex h-6 shrink-0 items-center gap-2 text-left"
                    onClick={() => setExpanded(current => !current)}
                    type="button"
                >
                    <span className="absolute -left-[22px] flex h-5 w-5 items-center justify-center text-slate-500 group-hover:text-indigo-400">
                        <span
                            className={`ml-1 h-0 w-0 border-y-[5px] border-l-[6px] border-y-transparent border-l-current transition-transform duration-150 ease-out ${expanded ? "rotate-90" : "rotate-0"}`}
                        />
                    </span>
                    {name !== undefined && <span className="text-slate-300"><SearchableKey name={name} query={normalizedSearchQuery} />:</span>}
                    <span className="font-semibold text-indigo-400">{openingBracket}</span>
                    <span className="shrink-0 whitespace-nowrap rounded-full bg-slate-800 px-2 font-sans text-xs leading-5 text-slate-400">
                        {entries.length} {entries.length === 1 ? "item" : "items"}
                    </span>
                    {!expanded && <span className="font-semibold text-indigo-400">{closingBracket}</span>}
                    <span className="ml-1 font-sans text-[11px] text-slate-500">{collectionType}</span>
                </button>
                <button
                    aria-label={`Copy ${name || collectionType}`}
                    className="ml-2 mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] text-slate-500 opacity-0 transition group-hover:opacity-100 hover:bg-indigo-500/10 hover:text-indigo-300 focus:opacity-100"
                    onClick={() => navigator.clipboard.writeText(formatJson(value))}
                    title={`Copy this ${collectionType.toLowerCase()}`}
                    type="button"
                >
                    <CopyOutlined />
                </button>
                {hasCollectionChildren && (
                    <button
                        aria-label={itemsExpanded ? "Close items" : "Expand items"}
                        className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-sans text-xs text-slate-500 transition hover:bg-indigo-500/10 hover:text-indigo-400 sm:w-auto sm:px-2 sm:text-[11px] sm:font-semibold sm:text-indigo-400 sm:opacity-70 sm:hover:opacity-100"
                        onClick={() => {
                            const nextItemsExpanded = !itemsExpanded;

                            setExpanded(true);
                            setVisibleCount(entries.length);
                            setItemsExpanded(nextItemsExpanded);
                            setExpandChildrenSignal(signal => signal + 1);
                        }}
                        title={`${itemsExpanded ? "Close" : "Expand"} all items in this array`}
                        type="button"
                    >
                        {itemsExpanded ? <ShrinkOutlined className="sm:hidden" /> : <ArrowsAltOutlined className="sm:hidden" />}
                        <span className="hidden sm:inline">{itemsExpanded ? "Close items" : "Expand items"}</span>
                    </button>
                )}
            </div>

            {expanded && (
                <div>
                    <div className="border-l border-slate-700 pl-4">
                        {visibleEntries.slice(0, visibleCount).map(([key, childValue]) => {
                            return (
                                <div className="pl-4" key={key}>
                                    <TreeNode
                                        activeSearchPath={activeSearchPath}
                                        defaultExpanded={depth < 1}
                                        depth={depth + 1}
                                        expandAllSignal={expandAllSignal}
                                        closeAllSignal={closeAllSignal}
                                        expandSelfSignal={isArray ? expandChildrenSignal : 0}
                                        expandSelfValue={itemsExpanded}
                                        name={key}
                                        onNavigateToValue={onNavigateToValue}
                                        path={[...path, isArray ? Number(key) : key]}
                                        searchQuery={normalizedSearchQuery}
                                        showAllSearchResults={showEntireObject}
                                        value={childValue}
                                    />
                                </div>
                            );
                        })}
                        {normalizedSearchQuery && !visibleEntries.length && (
                            <div className="ml-4 h-6 font-sans text-xs italic leading-6 text-slate-500">
                                No matching key or value
                            </div>
                        )}
                        {visibleCount < visibleEntries.length && (
                            <button
                                className="my-1 ml-4 rounded-md bg-indigo-500/10 px-3 py-1 font-sans text-xs font-semibold text-indigo-400 hover:bg-indigo-500/20"
                                onClick={() => setVisibleCount(count => count + CHILDREN_BATCH_SIZE)}
                                type="button"
                            >
                                Show next {Math.min(CHILDREN_BATCH_SIZE, visibleEntries.length - visibleCount)} of {visibleEntries.length}
                            </button>
                        )}
                    </div>
                    <div className="h-6 font-semibold leading-6 text-indigo-400">{closingBracket}</div>
                </div>
            )}
        </div>
    );
};

const TreeNode = memo(TreeNodeComponent);


interface ObjectTreeProps {
    parsedValue: JsonValue;
    input: string;
    error: string;
    isParsing: boolean;
    treeVersion: number;
    mobilePanel: "input" | "tree";
    activePageTab: string;
    hasApiResponse: boolean;
    hasSelectedApiEndpoint: boolean;
    isCallingApi: boolean;
    onNavigateToValue?: (path: Array<string | number>) => void;
}

const ObjectTree = ({ parsedValue, input, error, isParsing, treeVersion, mobilePanel, activePageTab, hasApiResponse, hasSelectedApiEndpoint, isCallingApi, onNavigateToValue }: ObjectTreeProps) => {
    const [copied, setCopied] = useState(false);
    const [expandAllSignal, setExpandAllSignal] = useState(0);
    const [closeAllSignal, setCloseAllSignal] = useState(0);

    useEffect(() => {
        setExpandAllSignal(0);
        setCloseAllSignal(0);
    }, [treeVersion]);

    const [treeSearch, setTreeSearch] = useState("");
    const deferredTreeSearch = useDeferredValue(treeSearch.trim().toLowerCase());
    const [searchResultIndex, setSearchResultIndex] = useState(0);
    const searchPaths = useMemo(() => collectSearchPaths(parsedValue, deferredTreeSearch), [parsedValue, deferredTreeSearch]);
    const activeSearchPath = searchPaths[searchResultIndex % (searchPaths.length || 1)];
    useEffect(() => {
        setSearchResultIndex(0);
    }, [deferredTreeSearch, parsedValue]);

    const moveSearchResult = (direction: number) => {
        setSearchResultIndex(index => (index + direction + searchPaths.length) % searchPaths.length);
    };
    const copyOutput = async () => {
        await navigator.clipboard.writeText(formatJson(parsedValue));
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
    };

    return (
        <section className={`${(activePageTab === "parse-object" && mobilePanel !== "tree") || (activePageTab === "apis" && !hasSelectedApiEndpoint && !hasApiResponse) ? "hidden sm:flex" : "flex"} ${activePageTab === "parse-object" ? "max-sm:min-h-0 max-sm:h-full" : ""} min-w-0 min-h-[420px] flex-col overflow-hidden rounded-lg border border-[#373b44] bg-[#24272e] shadow-xl shadow-black/20`}>
            <div className="flex min-h-[42px] shrink-0 flex-wrap items-center justify-between gap-1.5 py-[5px] border-b border-[#373b44] bg-[#202329] px-2.5 sm:min-h-[50px] sm:px-5 sm:py-2">
                <div>
                    <h2 className="m-0 text-base font-bold text-slate-100 pb-[2px]">Object tree</h2>
                </div>
                <div className="contents sm:flex sm:flex-wrap sm:items-center sm:gap-2">
                    <div className="order-last flex h-8 w-full items-center sm:order-none sm:w-72 gap-2 rounded-lg border border-slate-700 bg-slate-950 pl-2.5 text-slate-500 transition focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/15">
                        <SearchOutlined className="shrink-0 text-xs" />
                        <input
                            aria-label="Search object tree"
                            className="min-w-0 flex-1 bg-transparent text-xs text-slate-200 placeholder:text-slate-500"
                            onChange={event => setTreeSearch(event.target.value)}
                            onKeyDown={event => {
                                if (event.key === "Enter" && searchPaths.length) {
                                    event.preventDefault();
                                    moveSearchResult(event.shiftKey ? -1 : 1);
                                }
                            }}
                            placeholder="Search key / value"
                            type="search"
                            value={treeSearch}
                        />
                        {treeSearch.trim() && (
                            <div className="flex shrink-0 items-center gap-1">
                                <span aria-live="polite" className="min-w-[36px] text-center text-xs text-slate-400">
                                    {searchPaths.length ? (searchResultIndex % searchPaths.length) + 1 : 0}/{searchPaths.length}
                                </span>
                                {([-1, 1] as const).map(direction => (
                                    <button
                                        aria-label={direction === -1 ? "Previous tree match" : "Next tree match"}
                                        className="flex h-7 w-7 items-center justify-center rounded text-xs text-slate-400 hover:bg-indigo-500/10 hover:text-indigo-300 disabled:opacity-40"
                                        disabled={!searchPaths.length || isParsing || Boolean(error) || deferredTreeSearch !== treeSearch.trim().toLowerCase()}
                                        key={direction}
                                        onClick={() => moveSearchResult(direction)}
                                        title={direction === -1 ? "Previous match (Shift + Enter)" : "Next match (Enter)"}
                                        type="button"
                                    >
                                        {direction === -1 ? <UpOutlined /> : <DownOutlined />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        aria-label="Expand all"
                        title="Expand all"
                        className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent p-0 text-sm text-slate-500 transition hover:bg-indigo-500/10 hover:text-indigo-400 sm:border sm:border-slate-700 sm:bg-slate-900 sm:text-xs sm:font-semibold sm:text-slate-300 sm:hover:border-indigo-500 sm:hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-40 sm:ml-0 sm:w-auto sm:px-3"
                        disabled={isParsing || Boolean(error) || !input.trim()}
                        onClick={() => setExpandAllSignal(signal => signal + 1)}
                        type="button"
                    >
                        <ArrowsAltOutlined className="sm:hidden" />
                        <span className="hidden sm:inline">Expand all</span>
                    </button>
                    <button
                        aria-label="Close all"
                        title="Close all"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent p-0 text-sm text-slate-500 transition hover:bg-indigo-500/10 hover:text-indigo-400 sm:border sm:border-slate-700 sm:bg-slate-900 sm:text-xs sm:font-semibold sm:text-slate-300 sm:hover:border-indigo-500 sm:hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-3"
                        disabled={isParsing || Boolean(error) || !input.trim()}
                        onClick={() => setCloseAllSignal(signal => signal + 1)}
                        type="button"
                    >
                        <ShrinkOutlined className="sm:hidden" />
                        <span className="hidden sm:inline">Close all</span>
                    </button>
                    <button
                        aria-label={copied ? "Copied" : "Copy object"}
                        title={copied ? "Copied" : "Copy object"}
                        className="flex h-8 w-8 items-center justify-center gap-2 rounded-lg border-0 bg-transparent p-0 text-sm text-slate-500 transition hover:bg-indigo-500/10 hover:text-indigo-400 sm:border sm:border-slate-700 sm:bg-slate-900 sm:text-xs sm:font-semibold sm:text-slate-300 sm:hover:border-indigo-500 sm:hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-3"
                        disabled={isParsing || Boolean(error) || !input.trim()}
                        onClick={copyOutput}
                        type="button"
                    >
                        {copied ? <CheckOutlined className="text-emerald-600" /> : <CopyOutlined />}
                        <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
                    </button>
                </div>
            </div>
            <div className={`min-h-[320px] max-h-[65dvh] flex-1 overflow-auto overscroll-auto px-8 py-5 sm:max-h-none sm:px-9 sm:py-6 lg:min-h-0 ${activePageTab === "parse-object" ? "max-sm:min-h-0 max-sm:max-h-none" : ""}`}>
                {(activePageTab === "apis" && isCallingApi) || isParsing ? (
                    <div role="status" className="flex h-full min-h-[280px] flex-col justify-center gap-5 text-sm text-slate-400">
                        <div className="flex items-center justify-center gap-2">
                            <LoadingOutlined aria-hidden="true" className="animate-spin text-lg text-indigo-400 motion-reduce:animate-none" />
                            <span>{activePageTab === "apis" && isCallingApi ? "Calling API..." : "Parsing large object..."}</span>
                        </div>
                        <div aria-hidden="true" className="mx-auto w-full max-w-sm animate-pulse space-y-3 border-l border-slate-700/60 pl-4 motion-reduce:animate-none">
                            {["w-28", "ml-4 w-40", "ml-4 w-32", "ml-8 w-36", "w-24"].map(width => (
                                <div className={`flex h-4 items-center gap-2 ${width}`} key={width}>
                                    <span className="h-2 w-2 shrink-0 rounded-sm bg-indigo-400/30" />
                                    <span className="h-2.5 flex-1 rounded bg-slate-700/60" />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : activePageTab === "apis" && !hasApiResponse ? (
                    <div className="flex h-full min-h-[280px] items-center justify-center text-sm text-slate-400">
                        Select and call an API to view its response here.
                    </div>
                ) : error ? (
                    <div className="flex h-full min-h-[280px] flex-col items-center justify-center text-center">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-500/10 font-mono text-xl font-bold text-rose-400">!</div>
                        <p className="m-0 font-semibold text-slate-300">Unable to build the object tree</p>
                        <p className="mb-0 mt-1 max-w-sm text-sm text-slate-500">Correct the JSON syntax in the input panel to continue.</p>
                    </div>
                ) : !input.trim() ? (
                    <div className="flex h-full min-h-[280px] items-center justify-center text-sm text-slate-400">
                        Your object tree will appear here.
                    </div>
                ) : (
                    <div className="w-max min-w-full whitespace-nowrap">
                        <TreeNode
                            activeSearchPath={activeSearchPath}
                            closeAllSignal={closeAllSignal}
                            expandAllSignal={expandAllSignal}
                            key={treeVersion}
                            onNavigateToValue={activePageTab === "parse-object" ? onNavigateToValue : undefined}
                            searchQuery={deferredTreeSearch}
                            value={parsedValue}
                        />
                    </div>
                )}
            </div>
        </section>
    );
};

export default ObjectTree;
